/**
 * `useMaterialRequests` — live read of material requests for one
 * project, with optional status filter.
 *
 * Mirrors mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/materialRequests/useMaterialRequests.ts
 *
 * Path: `materialRequests where orgId == X AND projectId == Y`.
 * Sort: createdAt desc.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import type {
  MaterialRequest,
  MaterialRequestStatus,
} from '@/lib/materialRequests';

export type UseMaterialRequestsResult = {
  data: MaterialRequest[];
  loading: boolean;
};

export function useMaterialRequests(
  orgId: string | null | undefined,
  projectId: string | undefined,
  status?: MaterialRequestStatus | null,
): UseMaterialRequestsResult {
  const [all, setAll] = useState<MaterialRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !projectId) {
      setAll([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'materialRequests'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
      ),
      (snap) => {
        const rows: MaterialRequest[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<MaterialRequest, 'id'>),
        }));
        rows.sort((a, b) => {
          const am = a.createdAt?.toMillis?.() ?? 0;
          const bm = b.createdAt?.toMillis?.() ?? 0;
          return bm - am;
        });
        setAll(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useMaterialRequests] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [orgId, projectId]);

  const data = useMemo(() => {
    if (!status) return all;
    return all.filter((r) => r.status === status);
  }, [all, status]);

  return { data, loading };
}

/** Single-doc subscription for the request detail panel. */
export function useMaterialRequest(
  requestId: string | undefined,
): { data: MaterialRequest | null; loading: boolean } {
  const [data, setData] = useState<MaterialRequest | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!requestId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      doc(db, 'materialRequests', requestId),
      (snap) => {
        if (!snap.exists()) {
          setData(null);
        } else {
          setData({
            id: snap.id,
            ...(snap.data() as Omit<MaterialRequest, 'id'>),
          });
        }
        setLoading(false);
      },
      (err) => {
        console.warn('[useMaterialRequest] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [requestId]);

  return { data, loading };
}
